import {
  faArrowTrendUp,
  faBrain,
  faExclamationTriangle,
  faInbox,
  faLightbulb,
  faRefresh,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Doughnut, Line } from "react-chartjs-2";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";
import { useMeCore } from "../../cores/me";
import { socialIntelligenceApi } from "../../cores/socialIntelligence/api";
import { useSocialIntelligenceCore } from "../../cores/socialIntelligence";
import { SocialInteraction } from "../../cores/socialIntelligence/types";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import { PanelWembii } from "../component/PanelWembii";
import "./SocialIntelligence.scss";

type Tab = "overview" | "inbox" | "insights" | "operations";

const sentimentColor: Record<string, string> = {
  positive: "#26a69a",
  neutral: "#78909c",
  negative: "#ef5350",
};

const compact = (value: any) => Number(value || 0).toLocaleString("es-CL");

export const SocialIntelligence = () => {
  const { company, companyRole, isAdmin, isReseller } = useMeCore();
  const canAccess =
    Boolean(companyRole?.socialIntelligence || companyRole?.owner || companyRole?.admin || isAdmin || isReseller);
  const core = useSocialIntelligenceCore(company?.id, canAccess);
  const [tab, setTab] = useState<Tab>("overview");
  const [interactions, setInteractions] = useState<SocialInteraction[]>([]);
  const [page, setPage] = useState<any>(null);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sentiment, setSentiment] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const companyGeneration = useRef(0);

  useEffect(() => {
    companyGeneration.current += 1;
    setInteractions([]);
    setPage(null);
    setSelected(null);
    setDetailError(null);
  }, [company?.id]);

  const loadInteractions = async () => {
    if (!company?.id) return;
    setInboxLoading(true);
    const generation = companyGeneration.current;
    try {
      const result = await socialIntelligenceApi.interactions(String(company.id), {
        ...core.filters,
        page: 1,
        pageSize: 25,
        search: search || undefined,
        sentiment: sentiment || undefined,
        sortField: "sourceCreatedAt",
        sortDir: "DESC",
      });
      if (companyGeneration.current === generation) {
        setInteractions(result.data || []);
        setPage(result.pagination);
      }
    } finally {
      if (companyGeneration.current === generation) setInboxLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "inbox") loadInteractions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, core.filters.dateFrom, core.filters.dateTo, sentiment]);

  const openInteraction = async (interaction: SocialInteraction) => {
    if (!company?.id) return;
    setDetailError(null);
    try {
      const detail = await socialIntelligenceApi.detail(String(company.id), interaction.id);
      setSelected(detail);
    } catch {
      setDetailError("No pudimos abrir esta conversación. Intenta nuevamente.");
    }
  };

  const metrics = core.dashboard?.summary?.metrics || {};
  const distribution = core.dashboard?.sentiment?.distribution || {};
  const evolution = core.dashboard?.sentiment?.evolution || [];
  const topics = core.dashboard?.topics?.topics || [];
  const sources = core.dashboard?.summary?.availableSources || [];
  const risks = core.dashboard?.risks?.data || [];
  const opportunities = core.dashboard?.opportunities?.data || [];

  const sentimentChart = useMemo(
    () => ({
      labels: ["Positivo", "Neutral", "Negativo"],
      datasets: [
        {
          data: [distribution.positive || 0, distribution.neutral || 0, distribution.negative || 0],
          backgroundColor: [sentimentColor.positive, sentimentColor.neutral, sentimentColor.negative],
          borderWidth: 0,
        },
      ],
    }),
    [distribution]
  );
  const evolutionChart = useMemo(
    () => ({
      labels: evolution.map((item: any) => String(item.day || item.key || "").slice(5)),
      datasets: [
        {
          label: "Conversaciones",
          data: evolution.map((item: any) => item.count || 0),
          borderColor: "#0B65C2",
          backgroundColor: "rgba(11,101,194,.10)",
          pointRadius: 2,
          fill: true,
        },
      ],
    }),
    [evolution]
  );

  if (!canAccess) {
    return (
      <Layout><Content><PanelWembii className="si-empty si-access"><FontAwesomeIcon icon={faShieldHalved} /><h3>Inteligencia Social</h3><p>No tienes permisos para consultar esta información.</p></PanelWembii></Content></Layout>
    );
  }

  const updateStatus = async (status: string) => {
    if (!selected || !company?.id) return;
    try {
      await socialIntelligenceApi.patchInteraction(
        String(company.id),
        selected.interaction.id,
        { status }
      );
      // PATCH devuelve sólo una parte del detalle; refrescar evita asumir un objeto completo.
      const detail = await socialIntelligenceApi.detail(
        String(company.id),
        selected.interaction.id
      );
      setSelected(detail);
      loadInteractions();
    } catch {
      setDetailError("No pudimos actualizar esta conversación. Intenta nuevamente.");
    }
  };

  const reanalyze = async () => {
    if (!selected || !company?.id) return;
    try {
      await socialIntelligenceApi.reanalyze(
        String(company.id),
        selected.interaction.id
      );
      setSelected(null);
      loadInteractions();
    } catch {
      setDetailError("No pudimos solicitar el reanálisis. Intenta nuevamente.");
    }
  };

  return (
    <Layout>
      <Content>
        <div className="si-page">
          <header className="si-header">
            <div>
              <span className="si-eyebrow">INTELIGENCIA SOCIAL</span>
              <h1>La conversación, convertida en decisiones.</h1>
              <p>Una lectura clara de lo que tu audiencia necesita hoy.</p>
            </div>
            <div className="si-header-actions">
              <Input type="date" value={core.filters.dateFrom.slice(0, 10)} onChange={(event) => core.setFilters({ ...core.filters, dateFrom: new Date(event.target.value).toISOString() })} aria-label="Fecha inicial" />
              <Input type="date" value={core.filters.dateTo.slice(0, 10)} onChange={(event) => core.setFilters({ ...core.filters, dateTo: new Date(event.target.value).toISOString() })} aria-label="Fecha final" />
              <Button color="primary" onClick={core.refresh}><FontAwesomeIcon icon={faRefresh} /> Actualizar</Button>
            </div>
          </header>

          <nav className="si-tabs" aria-label="Secciones de inteligencia social">
            {([
              ["overview", "Panorama"],
              ["inbox", "Bandeja"],
              ["insights", "Temas y sentimiento"],
              ["operations", "Operación"],
            ] as [Tab, string][]).map(([id, label]) => (
              <button key={id} className={tab === id ? "active" : ""} onClick={() => setTab(id)}>{label}</button>
            ))}
          </nav>

          {core.loading ? <SocialSkeleton /> : core.error ? <PanelWembii className="si-empty"><h3>Algo no salió como esperábamos</h3><p>{core.error}</p><Button color="primary" onClick={core.refresh}>Reintentar</Button></PanelWembii> : (
            <>
              {tab === "overview" && (
                <>
                  <section className="si-kpis">
                    <Kpi title="Conversaciones" value={metrics.total} icon={faInbox} trend={core.dashboard?.summary?.variation?.total} />
                    <Kpi title="Analizadas" value={metrics.analyzed} icon={faBrain} />
                    <Kpi title="Oportunidades" value={metrics.opportunities} icon={faLightbulb} accent="mint" />
                    <Kpi title="Riesgos posibles" value={metrics.possibleCrisis} icon={faExclamationTriangle} accent="danger" />
                    <Kpi
                      title="Tiempo de respuesta"
                      value={metrics.avgResponseTimeMs}
                      emptyValue="Sin datos suficientes"
                      icon={faArrowTrendUp}
                    />
                  </section>
                  <section className="si-grid si-grid--hero">
                    <PanelWembii className="si-panel si-evolution"><PanelTitle title="Pulso de la conversación" subtitle="Volumen durante el período seleccionado" /><Line data={evolutionChart} options={chartOptions} /></PanelWembii>
                    <PanelWembii className="si-panel si-sentiment"><PanelTitle title="Sentimiento" subtitle="Cómo se siente tu audiencia" /><Doughnut data={sentimentChart} options={{ legend: { display: false }, cutoutPercentage: 72 }} /><SentimentLegend distribution={distribution} /></PanelWembii>
                  </section>
                  <section className="si-grid si-grid--split">
                    <PanelWembii className="si-panel"><PanelTitle title="Temas que mueven la conversación" subtitle="Señales detectadas por IA" />{topics.length ? <TopicList topics={topics.slice(0, 5)} /> : <Empty text="Aún no hay temas suficientes para analizar." />}</PanelWembii>
                    <AiSummary
                      report={core.report}
                      reportError={core.reportError}
                      onRequest={core.requestExecutiveSummary}
                    />
                  </section>
                </>
              )}
              {tab === "inbox" && (
                <section className="si-inbox">
                  <PanelWembii className="si-panel si-filterbar">
                    <Input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === "Enter" && loadInteractions()} placeholder="Buscar en conversaciones…" aria-label="Buscar conversaciones" />
                    <Input type="select" value={sentiment} onChange={(event) => setSentiment(event.target.value)} aria-label="Filtrar por sentimiento"><option value="">Todos los sentimientos</option><option value="positive">Positivo</option><option value="neutral">Neutral</option><option value="negative">Negativo</option></Input>
                    <Button color="primary" onClick={loadInteractions}>Aplicar filtros</Button>
                  </PanelWembii>
                  <PanelWembii className="si-panel si-table-panel"><PanelTitle title="Bandeja unificada" subtitle={page ? `${page.total} conversaciones encontradas` : "Conversaciones de tu comunidad"} />{inboxLoading ? <SocialSkeleton /> : interactions.length ? <InteractionTable rows={interactions} onSelect={openInteraction} /> : <Empty text="No encontramos conversaciones con estos filtros." />}</PanelWembii>
                </section>
              )}
              {tab === "insights" && (
                <section className="si-grid si-grid--split">
                  <PanelWembii className="si-panel"><PanelTitle title="Mapa de temas" subtitle="Lo que más se repite en tu audiencia" />{topics.length ? <TopicList topics={topics} /> : <Empty text="Aún no hay temas analizados." />}</PanelWembii>
                  <PanelWembii className="si-panel"><PanelTitle title="Sentimiento por período" subtitle="Evolución de la percepción" /><Line data={evolutionChart} options={chartOptions} /><div className="si-source-chips">{sources.map((source: string) => <span key={source}>{source.replace("_", " ")}</span>)}</div></PanelWembii>
                </section>
              )}
              {tab === "operations" && (
                <section className="si-grid si-grid--split">
                  <PanelWembii className="si-panel"><PanelTitle title="Oportunidades detectadas" subtitle="Conversaciones con potencial comercial" /><InteractionTable rows={opportunities.slice(0, 6)} onSelect={openInteraction} compact /></PanelWembii>
                  <PanelWembii className="si-panel"><PanelTitle title="Riesgos posibles" subtitle="Señales que merecen atención" /><InteractionTable rows={risks.slice(0, 6)} onSelect={openInteraction} compact /><div className="si-job-status"><b>Cola operativa</b><span>{compact(core.dashboard?.jobs?.pending)} pendientes</span><span>{compact(core.dashboard?.jobs?.processing)} en proceso</span></div></PanelWembii>
                </section>
              )}
            </>
          )}
        </div>
        {detailError && <div className="si-inline-error" role="alert">{detailError}</div>}
        {selected && <InteractionDetail detail={selected} onClose={() => setSelected(null)} onUpdate={updateStatus} onReanalyze={reanalyze} />}
      </Content>
    </Layout>
  );
};

const chartOptions = { legend: { display: false }, maintainAspectRatio: false, responsive: true, scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, gridLines: { color: "rgba(15,55,90,.07)" } }], xAxes: [{ gridLines: { display: false } }] } };
const Kpi = ({ title, value, icon, trend, accent = "", emptyValue }: any) => <PanelWembii className={`si-kpi ${accent}`}><div><span>{title}</span><strong className={value === null || value === undefined ? "si-kpi-empty" : ""}>{value === null || value === undefined ? emptyValue || "Sin datos suficientes" : compact(value)}</strong>{typeof trend === "number" && <small className={trend >= 0 ? "up" : "down"}><FontAwesomeIcon icon={faArrowTrendUp} /> {Math.abs(trend)}% vs. período anterior</small>}</div><i><FontAwesomeIcon icon={icon} /></i></PanelWembii>;
const PanelTitle = ({ title, subtitle }: any) => <div className="si-panel-title"><div><h2>{title}</h2><p>{subtitle}</p></div></div>;
const Empty = ({ text }: { text: string }) => <div className="si-empty"><FontAwesomeIcon icon={faBrain} /><p>{text}</p></div>;
const SocialSkeleton = () => <div className="si-skeleton"><i /><i /><i /><i /></div>;
const SentimentLegend = ({ distribution }: any) => <div className="si-sentiment-legend">{["positive", "neutral", "negative"].map((key) => <div key={key}><i style={{ backgroundColor: sentimentColor[key] }} /><span>{{ positive: "Positivo", neutral: "Neutral", negative: "Negativo" }[key]}</span><b>{compact(distribution[key])}</b></div>)}</div>;
const TopicList = ({ topics }: any) => <div className="si-topic-list">{topics.map((topic: any, index: number) => <div key={`${topic.topic}-${index}`}><span className="rank">{String(index + 1).padStart(2, "0")}</span><div><b>{topic.topic}</b><small>{topic.sources?.join(" · ") || "Sin fuente"}</small></div><strong>{compact(topic.count)}</strong></div>)}</div>;
const InteractionTable = ({ rows, onSelect, compact = false }: any) => <div className={`si-interactions ${compact ? "compact" : ""}`}>{rows.map((row: SocialInteraction) => <button key={row.id} onClick={() => onSelect({ interaction: row })}><span className={`si-dot ${row.sentiment || "neutral"}`} /><div><b>{row.text || "Sin texto disponible"}</b><small>{row.source} · {row.category || "Sin categoría"}</small></div><em className={`priority ${row.priority}`}>{row.priority || "media"}</em></button>)}</div>;
const AiSummary = ({ report, reportError, onRequest }: any) => <PanelWembii className="si-panel si-ai-summary"><PanelTitle title="Lectura ejecutiva con IA" subtitle="Una síntesis de lo que merece atención" />{report?.status === "completed" ? <><h3>{report.data?.headline}</h3><p>{report.summary}</p>{report.data?.recommendations?.slice(0, 3).map((item: string) => <div className="si-ai-recommendation" key={item}>{item}</div>)}</> : <><p>{reportError || (report?.status === "failed" ? "No pudimos generar el resumen. Puedes volver a solicitarlo." : "Convierte métricas y señales en una lectura ejecutiva para tomar mejores decisiones.")}</p><Button color="primary" disabled={report?.status === "pending" || report?.status === "processing"} onClick={onRequest}>{report?.status === "pending" || report?.status === "processing" ? <><Spinner size="sm" /> Analizando…</> : "Generar resumen"}</Button></>}</PanelWembii>;
const InteractionDetail = ({ detail, onClose, onUpdate, onReanalyze }: any) => <div className="si-drawer-backdrop"><aside className="si-drawer"><Button close onClick={onClose} aria-label="Cerrar detalle" /><span className="si-eyebrow">CONVERSACIÓN</span><h2>{detail.interaction.source}</h2><p>{detail.interaction.text}</p><div className="si-detail-meta"><span>{detail.interaction.sentiment || "neutral"}</span><span>{detail.interaction.category || "Sin categoría"}</span></div><h4>Acciones rápidas</h4><div className="si-drawer-actions"><Button outline color="primary" onClick={() => onUpdate("en_revision")}>Marcar en revisión</Button><Button outline color="primary" onClick={() => onUpdate("resuelto")}>Resolver</Button><Button color="primary" onClick={onReanalyze}>Reanalizar con IA</Button></div></aside></div>;
